import { Check, Loader2, PenLine, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "sonner";

import type {
  AssistantGeneratedAssetCopyEditOption,
  AssistantGeneratedAssetCopyEditPreviewReference,
} from "@/lib/assistant-generated-asset-copy-edit-previews";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { Button } from "@runcheetah/ui";

type ProposalState = "active" | "cancelled" | "applied";

export function AssistantGeneratedAssetCopyEditPreviews({
  brandId,
  chatId,
  references,
  isStale = false,
  onOpenEditor,
}: {
  brandId: string;
  chatId: string | null;
  references: AssistantGeneratedAssetCopyEditPreviewReference[];
  isStale?: boolean;
  onOpenEditor?: (reference: AssistantGeneratedAssetCopyEditPreviewReference) => void;
}) {
  if (references.length === 0) {
    return null;
  }

  return (
    <div className="w-full space-y-3">
      {references.map((reference) => (
        <AssistantGeneratedAssetCopyEditPreviewBanner
          key={reference.id}
          brandId={brandId}
          chatId={chatId}
          reference={reference}
          isStale={isStale}
          onOpenEditor={onOpenEditor}
        />
      ))}
    </div>
  );
}

function AssistantGeneratedAssetCopyEditPreviewBanner({
  brandId,
  chatId,
  reference,
  isStale,
  onOpenEditor,
}: {
  brandId: string;
  chatId: string | null;
  reference: AssistantGeneratedAssetCopyEditPreviewReference;
  isStale: boolean;
  onOpenEditor?: (reference: AssistantGeneratedAssetCopyEditPreviewReference) => void;
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const [proposalState, setProposalState] = useState<ProposalState>("active");
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const selectedOption = reference.options[selectedOptionIndex] ?? reference.options[0];
  const cancelled = proposalState === "cancelled";
  const applied = proposalState === "applied";
  const closed = isStale && proposalState === "active";
  const applyCopyEdit = trpc.brandAssistantChat.applyGeneratedAssetCopyEdit.useMutation({
    onSuccess: async () => {
      setProposalState("applied");
      setError(null);
      await Promise.all([
        chatId
          ? utils.brandAssistantChat.generatedAssets.invalidate({ brandId, chatId })
          : Promise.resolve(),
        utils.calendar.list.invalidate(),
        reference.batchSourceType === "campaign"
          ? utils.campaign.get.invalidate({ brandId, id: reference.batchId })
          : reference.batchSourceType === "standalone"
            ? utils.standaloneAssetBatch.get.invalidate({ brandId, id: reference.batchId })
            : Promise.resolve(),
      ]);
      toast.success(
        intl.formatMessage({
          id: "assistantGeneratedAssetCopyEditPreview.applySuccess",
          defaultMessage: "Copy updated",
        }),
      );
    },
    onError: (mutationError) => setError(mutationError.message),
  });
  const canApply = Boolean(chatId && selectedOption && !closed && !cancelled && !applied);

  const applySelectedOption = () => {
    if (!canApply || !selectedOption || !chatId || applyCopyEdit.isPending) {
      return;
    }

    applyCopyEdit.mutate({
      brandId,
      chatId,
      generatedAssetId: reference.generatedAssetId,
      scheduledPostId: reference.scheduledPostId,
      blockId: reference.blockId,
      caption: selectedOption.caption,
      hashtags: selectedOption.hashtags,
      expectedDraftUpdatedAt: reference.draftUpdatedAt,
    });
  };

  if (cancelled) {
    return (
      <div className="w-full rounded-3xl border border-border/70 bg-muted/35 px-4 py-3 text-sm text-muted-foreground">
        <FormattedMessage
          id="assistantGeneratedAssetCopyEditPreview.cancelled"
          defaultMessage="Copy edit proposal cancelled."
        />
      </div>
    );
  }

  return (
    <article className="w-full border-y border-border/60 bg-transparent">
      <div className="space-y-4 p-4">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground/88">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center text-muted-foreground">
              <PenLine className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            <FormattedMessage
              id="assistantGeneratedAssetCopyEditPreview.title"
              defaultMessage="Update this post copy?"
            />
          </div>
          <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
            {reference.summary ?? reference.instruction}
          </p>
        </div>

        <CopyBlock
          label={
            <FormattedMessage
              id="assistantGeneratedAssetCopyEditPreview.currentLabel"
              defaultMessage="Current"
            />
          }
          caption={reference.currentCaption}
          hashtags={reference.currentHashtags}
          emptyLabel={
            <FormattedMessage
              id="assistantGeneratedAssetCopyEditPreview.noCurrentCopy"
              defaultMessage="No saved caption found."
            />
          }
          nullHashtagLabel={
            <FormattedMessage
              id="assistantGeneratedAssetCopyEditPreview.noCurrentHashtags"
              defaultMessage="No saved hashtags found."
            />
          }
        />

        {error ? (
          <div className="rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        {closed ? (
          <ProposalStatus>
            <X className="text-muted-foreground/40" size="16" aria-hidden="true" />
            <span className="font-medium text-foreground/58">
              <FormattedMessage
                id="assistantGeneratedAssetCopyEditPreview.supersededLabel"
                defaultMessage="This proposal can no longer be applied because the conversation has moved forward."
              />
            </span>
          </ProposalStatus>
        ) : applied ? (
          <ProposalStatus>
            <Check className="text-emerald-600" size="16" aria-hidden="true" />
            <span className="font-medium text-foreground/58">
              <FormattedMessage
                id="assistantGeneratedAssetCopyEditPreview.appliedLabel"
                defaultMessage="Copy update applied."
              />
            </span>
          </ProposalStatus>
        ) : (
          <div className="space-y-3 border-t border-border/60 pt-3">
            <div className="grid gap-2" role="radiogroup">
              {reference.options.map((option, index) => (
                <CopyOptionButton
                  key={`${reference.id}:${index}`}
                  option={option}
                  selected={index === selectedOptionIndex}
                  onSelect={() => setSelectedOptionIndex(index)}
                />
              ))}
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                className="rounded-full text-foreground/65"
                disabled={applyCopyEdit.isPending}
                onClick={() => setProposalState("cancelled")}
              >
                <X className="h-4 w-4" aria-hidden="true" />
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              {onOpenEditor ? (
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  disabled={applyCopyEdit.isPending}
                  onClick={() => onOpenEditor(reference)}
                >
                  <FormattedMessage
                    id="assistantGeneratedAssetCopyEditPreview.openEditor"
                    defaultMessage="Open editor"
                  />
                </Button>
              ) : null}
              <Button
                type="button"
                className="rounded-full"
                disabled={!canApply || applyCopyEdit.isPending}
                onClick={applySelectedOption}
              >
                {applyCopyEdit.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                <FormattedMessage
                  id="assistantGeneratedAssetCopyEditPreview.applyFree"
                  defaultMessage="Apply free"
                />
              </Button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function CopyOptionButton({
  option,
  selected,
  onSelect,
}: {
  option: AssistantGeneratedAssetCopyEditOption;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={cn(
        "min-w-0 rounded-2xl border bg-card/60 p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
        selected
          ? "border-primary/45 bg-primary/7 shadow-sm"
          : "border-border/70 hover:border-foreground/20 hover:bg-card",
      )}
      onClick={onSelect}
    >
      <CopyBlock caption={option.caption} hashtags={option.hashtags} />
    </button>
  );
}

function CopyBlock({
  label,
  caption,
  hashtags,
  emptyLabel,
  nullHashtagLabel,
}: {
  label?: ReactNode;
  caption: string | null;
  hashtags: string[] | null;
  emptyLabel?: ReactNode;
  nullHashtagLabel?: ReactNode;
}) {
  return (
    <div className="min-w-0 space-y-2">
      {label ? <div className="text-xs font-semibold text-muted-foreground">{label}</div> : null}
      {caption ? (
        <p className="whitespace-pre-wrap text-sm leading-6 text-foreground/88">{caption}</p>
      ) : (
        <p className="text-sm leading-6 text-muted-foreground">{emptyLabel}</p>
      )}
      <HashtagLine hashtags={hashtags} nullLabel={nullHashtagLabel} />
    </div>
  );
}

function HashtagLine({
  hashtags,
  nullLabel,
}: {
  hashtags: string[] | null;
  nullLabel?: ReactNode;
}) {
  if (hashtags === null) {
    return (
      <p className="text-xs font-medium text-muted-foreground">
        {nullLabel ?? (
          <FormattedMessage
            id="assistantGeneratedAssetCopyEditPreview.hashtagsUnchanged"
            defaultMessage="Hashtags unchanged"
          />
        )}
      </p>
    );
  }

  if (hashtags.length === 0) {
    return (
      <p className="text-xs font-medium text-muted-foreground">
        <FormattedMessage
          id="assistantGeneratedAssetCopyEditPreview.noHashtags"
          defaultMessage="No hashtags"
        />
      </p>
    );
  }

  return (
    <p className="break-words text-xs font-medium text-muted-foreground">
      {hashtags.map((tag) => (tag.startsWith("#") ? tag : `#${tag}`)).join(" ")}
    </p>
  );
}

function ProposalStatus({ children }: { children: ReactNode }) {
  return (
    <div className="border-t border-border/60 pt-3 text-sm text-muted-foreground">
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}
